import React from 'react';
import type { MotionValue } from 'framer-motion';
import type {
    AudioBands,
    LatentBackgroundTuning,
    MonetBackgroundImage,
    MonetBackgroundTuning,
    NomandBackgroundTuning,
    Theme,
    UrlBackgroundItem,
    VisualizerBackgroundMode,
} from '../../../types';

// src/components/visualizer/backgrounds/definition.ts
// Shared contracts for discoverable visualizer background modes.

export interface VisualizerBackgroundConfig {
    mode?: VisualizerBackgroundMode | null;
    transparent?: boolean;
    common?: {
        useCoverColorBg?: boolean;
        opacity?: number;
        disableGeometricBackground?: boolean;
        disableVignette?: boolean;
    };
    customImage?: MonetBackgroundImage | null;
    monet?: {
        tuning?: MonetBackgroundTuning;
    };
    nomand?: {
        tuning?: NomandBackgroundTuning;
    };
    latent?: {
        tuning?: LatentBackgroundTuning;
    };
    url?: {
        items?: UrlBackgroundItem[];
        selectedId?: string | null;
    };
}

export interface VisualizerBackgroundActions {
    onModeChange?: (mode: VisualizerBackgroundMode) => void;
    common?: {
        onCoverColorChange?: (enabled: boolean) => void;
        onOpacityChange?: (opacity: number) => void;
        onDisableGeometricChange?: (disabled: boolean) => void;
        onDisableVignetteChange?: (disabled: boolean) => void;
    };
    customImage?: {
        onUpload?: (files: File[]) => Promise<{ ok: boolean; error?: string; }>;
        onClear?: () => Promise<void> | void;
        isLoading?: boolean;
    };
    monet?: {
        onTuningChange?: (patch: Partial<MonetBackgroundTuning>) => void;
        onResetTuning?: () => void;
    };
    nomand?: {
        onTuningChange?: (patch: Partial<NomandBackgroundTuning>) => void;
        onResetTuning?: () => void;
    };
    latent?: {
        onTuningChange?: (patch: Partial<LatentBackgroundTuning>) => void;
        onResetTuning?: () => void;
    };
    url?: {
        onAdd?: (item: UrlBackgroundItem) => void;
        onUpdate?: (id: string, patch: Partial<Omit<UrlBackgroundItem, 'id'>>) => void;
        onDelete?: (id: string) => void;
        onSelect?: (id: string | null) => void;
    };
}

export interface VisualizerBackgroundRenderProps {
    config?: VisualizerBackgroundConfig;
    theme: Theme;
    isDaylight: boolean;
    coverUrl?: string | null;
    audioPower: MotionValue<number>;
    audioBands: AudioBands;
    seed?: string | number;
    staticMode: boolean;
    paused: boolean;
}

export interface VisualizerBackgroundSettingsProps {
    config?: VisualizerBackgroundConfig;
    actions?: VisualizerBackgroundActions;
    t: (key: string) => string;
    isDaylight: boolean;
    theme: Theme;
    controlCardBg: string;
    rangeInputClass: string;
    onSliderPointerDown?: () => void;
    onSliderCommit?: () => void;
}

/** 播放面板背景行里该模式专属的快捷参数，只放一到两个最常调的项。 */
export interface VisualizerBackgroundQuickControlsProps {
    config?: VisualizerBackgroundConfig;
    actions?: VisualizerBackgroundActions;
    t: (key: string) => string;
    isDaylight: boolean;
    theme: Theme;
}

export interface VisualizerBackgroundRegistryEntry {
    mode: VisualizerBackgroundMode;
    order: number;
    labelKey: string;
    labelFallback: string;
    render: (props: VisualizerBackgroundRenderProps) => React.ReactNode;
    renderSettingsPanel?: (props: VisualizerBackgroundSettingsProps) => React.ReactNode;
    renderQuickControls?: (props: VisualizerBackgroundQuickControlsProps) => React.ReactNode;
    resetSettings?: (actions?: VisualizerBackgroundActions) => void;
}

export interface VisualizerBackgroundEntryModule {
    default: VisualizerBackgroundRegistryEntry;
}

export const defineVisualizerBackground = (entry: VisualizerBackgroundRegistryEntry) => entry;
